import { useToast } from "@chakra-ui/react";

import {
  AddJson,
  BBox,
  Bundle,
  defaultTopZIndex,
  Dictionary,
  getAIHost,
  getHash,
  getRandomHash,
  IImageNode,
  ImageURLs,
  IRectangleShapeNode,
  ISingleNode,
  isUndefined,
  Lang,
  Logger,
  loadImage,
  Remove,
  safeCall,
  shallowCopy,
  Update,
  uploadImage,
  Graph,
  range,
  getRandomSeed,
  sleep,
} from "@noli/core";
import {
  exportBlob,
  ExportBlobOptions,
  exportSingleNodeBlob,
  ExportSingleNodeBlobOptions,
} from "@noli/svg";
import {
  BoardStore,
  BusinessOpCallbacks,
  langStore,
  safeClearExecuterStack,
  translate,
  useSafeExecute,
} from "@noli/business";

import type { AIMetaType, MetaType } from "@/types/meta";
import {
  addNewImage,
  getNewImageNode,
  autoArrange,
  rectangleToImage,
  toast,
  VirtualInfo,
  getImgWH,
  checkAIMeta,
} from "./misc";
import { DREAMLIKE_VERSION, imageLoadingGIFs } from "./constants";
import events, { EventCallback, EventInfo, Events } from "./events";
import { TaskDataType, tasksStore, TaskType, TrivialTaskEvent } from "@/stores/tasks";
import { parametersStore } from "@/stores/parameters";
import { accelerationStore } from "@/stores/acceleration";
import { loginStore } from "@/modules/login/store";
import { AI_CREATOR_APIs, APIs_Img2Img, APIs_Txt2Img } from "@/modules/editor/request/api";
import { Editor_Words } from "./lang/editor";
import { replaceAll } from "./string";
import { CloudProjectType } from "@/types/cloud-project";
import { PostCloudProjectResponse } from "@/types/cache";
import { toast as toastFn } from "@/components/Toast";
import { batchGeneratePolling, BatchTaskPolling, BatchTaskType, BatchTaskEvent } from "./batchTask";
import { generateUUID, getRandomInt } from "./random";
import { GenerateBizType } from "@/types/creator";
import { loadJson } from "./cloud-local-project";
import { CaptchaEvent } from "@/modules/global/components/CaptchaModal";

export type SetupPostCreatorProps = [
  ReturnType<typeof useToast>,
  string,
  Lang,
  string[],
  VirtualInfo[],
  boolean | undefined,
];
function getTaskHash(busyAliases: string[]): string {
  return getHash(busyAliases.sort().join("-")).toString();
}

function getTaskType(uri: string, data: Dictionary<any>): string {
  if (uri === APIs_Txt2Img["sd.inpainting"]) return "sd.inpainting";
  if (uri === APIs_Txt2Img["sd.outpainting"]) return "outpainting";
  if (uri === APIs_Img2Img.inpainting) return "inpainting";
  if (uri === APIs_Img2Img.semantic2img) return "semantic2img";
  if (uri === APIs_Txt2Img.sd)
    return Array.isArray(data.variations) && data.variations.length > 0
      ? "txt2img.variation"
      : "txt2img";
  if (uri === APIs_Img2Img.sd)
    return Array.isArray(data.variations) && data.variations.length > 0
      ? "img2img.variation"
      : "img2img";
  if (uri === APIs_Img2Img.sr) return data.is_anime ? "sr.anime" : "sr";
  return "unknown";
}

export function postDrawRecord(url: string, duration: number, meta: MetaType): void {
  safeCall<Response | void, any>(
    () => {
      if (loginStore.isGuest) return Promise.resolve();
      return fetch(`${process.env.REACT_APP_MAIN_URL}${AI_CREATOR_APIs.drawCreate}`, {
        method: "POST",
        headers: loginStore.jsonPostReqHeaders,
        body: JSON.stringify({
          drawings: [
            {
              meta_json: meta,
              image_url: url,
              is_ai: checkAIMeta(meta) ? 1 : 2,
              wait_time: duration,
            },
          ],
        }),
      });
    },
    {
      success: async () => void 0,
      failed: async (e) => Logger.error(`failed to postDrawRecord: ${e}`),
    },
  );
}

type OperationType = "download";
interface OperationData {
  download: {
    urls: (string | null | (string | null)[])[];
    metas: (MetaType | MetaType[])[];
  };
}
export function postOperation<T extends OperationType>(type: T, data: OperationData[T]): void {
  safeCall(
    () =>
      fetch(`${process.env.REACT_APP_MAIN_URL}${AI_CREATOR_APIs.operateCreate}`, {
        method: "POST",
        headers: loginStore.jsonPostReqHeaders,
        body: JSON.stringify({ operate_type: type, operate_data: data }),
      }),
    {
      success: async () => void 0,
      failed: async (e) => Logger.error(`failed to postOperation: ${e}`),
    },
  );
}

const baseInterval = 1000;

async function polling(status: StatusType, taskPolling: TaskPolling): Promise<TaskDataType | void> {
  const cleanup = async () => {
    if (tasksStore.tasks.has(taskPolling.key)) {
      tasksStore.removeTask(taskPolling);
    } else {
      Logger.error(`cannot find '${taskPolling.key}' in 'tasksStore', which is problematic`);
    }
    if (taskPollingPool.has(taskPolling.key)) {
      taskPollingPool.remove(taskPolling.key);
    } else {
      Logger.error(`cannot find '${taskPolling.key}' in 'taskPollingPool', which is problematic`);
    }
  };

  tasksStore.addTask(taskPolling);
  taskPollingPool.push(taskPolling);
  return safeCall(() => taskPolling.run(status), {
    success: cleanup,
    failed: cleanup,
  });
}
export class TaskPolling {
  t: ReturnType<typeof useToast>;
  lang: Lang;
  key: TaskType["key"];
  type: TaskType["type"];
  isKafka: TaskType["isKafka"];
  taskPack: TaskType["taskPack"];
  status: TaskType["status"];
  pending: TaskType["pending"];
  forceGuest = false;

  private timerId: any | null = null;
  private getStatusFailedCounter = 0;
  private getStatusFailedPatience = 3;

  resolve: (value: TaskDataType | PromiseLike<TaskDataType>) => void = () => void 0;
  reject: (reason?: any) => void = () => void 0;

  constructor(props: TaskType & { t: ReturnType<typeof useToast>; lang: Lang }) {
    this.t = props.t;
    this.lang = props.lang;
    this.key = props.key;
    this.isKafka = props.isKafka;
    this.taskPack = props.taskPack;
    this.type = props.type;
    this.status = props.status;
    this.pending = props.pending;
  }

  cancelLastCheck = () => {
    clearTimeout(this.timerId);
  };

  updateTaskKey = (key: TaskType["key"]) => {
    if (tasksStore.tasks.has(this.key)) {
      tasksStore.tasks.remove(this.key);
    }
    if (taskPollingPool.has(this.key)) {
      taskPollingPool.remove(this.key);
    }
    this.key = key;
    tasksStore.tasks.push(this);
    taskPollingPool.push(this);
    this.getStatusFailedCounter = 0;
    this.cancelLastCheck();
    this.timerId = setTimeout(this.check, 0);
  };

  check = async (): Promise<void> => {
    const status = await SafeOp.getKafkaStatus(this.key, this.forceGuest);

    if (!status) {
      Logger.warn("failed to get status, retrying...");
      this.getStatusFailedCounter += 1;
      if (this.getStatusFailedCounter >= this.getStatusFailedPatience) throw Error;
      this.timerId = setTimeout(this.check, baseInterval);
      return;
    }
    const newTask: TaskType = {
      key: this.key,
      type: this.type,
      isKafka: this.isKafka,
      taskPack: shallowCopy(this.taskPack),
      ...status,
    };
    tasksStore.updateTask(newTask);

    if (newTask.status === "finished") {
      this.resolve(newTask.data as TaskDataType);
    } else if (newTask.status === "exception") {
      this.reject((newTask.data as any as { reason: string }).reason);
    } else {
      this.timerId = setTimeout(this.check, this.getInterval(newTask));
    }
  };

  run = (status: StatusType) => {
    return new Promise<TaskDataType | PromiseLike<TaskDataType>>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
      this.getStatusFailedCounter = 0;

      this.timerId = setTimeout(() => {
        this.check();
      }, this.getInterval(status));
    });
  };

  getInterval(status: StatusType): number {
    const base = baseInterval * (1.0 + Math.random());
    if (status.status === "working") return base;
    const factor = Math.max(1.0, status.pending);
    if (factor >= 20) {
      toast(
        this.t,
        "info",
        translate(Editor_Words["on-fire-message"], this.lang),
        status.pending * 1000,
      );
    }
    return factor * base;
  }
}
export const taskPollingPool = new Bundle<TaskPolling>([]);

async function refreshVirtualNode(
  t: ReturnType<typeof useToast>,
  lang: Lang,
  alias: string,
  numFrame?: number,
): Promise<void> {
  const node = BoardStore.board.getNode(alias);
  if (node) {
    node.markAllDirty();
    await BoardStore.board.refresh(true);
  }
  const rePositionVirtualNodes = BoardStore.graph.allSingleNodes.filter(
    (node) => node.meta.isVirtual && node.meta.needRePosition,
  );
  if (rePositionVirtualNodes.length <= 1) return;
  await autoArrange(t, lang, rePositionVirtualNodes, {
    trace: false,
    numFrame: numFrame ?? 20,
    schedule: "easeOutQuad",
  });
}

function getVirtualNode(lang: Lang, virtualInfo: VirtualInfo, virtualAlias?: string) {
  const virtualNode = virtualInfo.type
    ? rectangleToImage(virtualInfo.snapshot(), imageLoadingGIFs[lang], virtualAlias)
    : getNewImageNode(virtualAlias!, imageLoadingGIFs[lang], virtualInfo);
  virtualNode.meta = { isVirtual: true, needRePosition: !virtualInfo.type };
  virtualNode.noSave = true;
  virtualNode.editable = false;
  virtualNode.zIndex = defaultTopZIndex;
  virtualNode.renderParams.borderColor = "#797979";
  virtualNode.renderParams.borderWidth = 5;
  virtualNode.renderParams.borderDashed = true;
  virtualNode.renderParams.borderBackground = "#3d3d3d";
  virtualNode.renderParams.renderType = "cover";
  return virtualNode;
}

export async function addVirtualNode(
  t: ReturnType<typeof useToast>,
  uri: string,
  lang: Lang,
  virtualInfo: VirtualInfo,
  numFrame?: number,
): Promise<string> {
  const executer = BoardStore.board.executer;
  // 失焦所有节点
  // await executer.board.selectorPluginNullable?.destroyAll(true);
  if (!virtualInfo.type) {
    const virtualAlias = `loading${replaceAll(uri, "/", ".")}.${getRandomHash()}`;
    const loadingNode = getVirtualNode(lang, virtualInfo, virtualAlias);
    await new AddJson().applyWith(executer, {
      aliases: [],
      params: { alias: virtualAlias, json: loadingNode.toJson() },
    });
    refreshVirtualNode(t, lang, virtualAlias, numFrame);
    return virtualAlias;
  }
  const virtualAlias = `.virtual.${virtualInfo.alias}`;
  const originAlias = virtualInfo.alias;
  const loadingNode = getVirtualNode(lang, virtualInfo, virtualAlias).snapshot();
  useSafeExecute("remove", null, true)([originAlias]);
  useSafeExecute("addJson", null, false)({ alias: virtualAlias, json: loadingNode.toJson() });
  refreshVirtualNode(t, lang, virtualAlias, numFrame);
  return virtualAlias;
}

export async function addBatchVirtualNode(
  t: ReturnType<typeof useToast>,
  uri: string,
  lang: Lang,
  virtualInfo: VirtualInfo,
  numFrame?: number,
): Promise<string> {
  const executer = BoardStore.board.executer;
  // 失焦所有节点
  // await executer.board.selectorPluginNullable?.destroyAll(true);
  if (!virtualInfo.type) {
    const virtualAlias = `loading${replaceAll(uri, "/", ".")}.${getRandomHash()}`;
    const loadingNode = getVirtualNode(lang, virtualInfo, virtualAlias);

    await new AddJson().applyWith(executer, {
      aliases: [],
      params: { alias: virtualAlias, json: loadingNode.toJson() },
    });
    refreshVirtualNode(t, lang, virtualAlias, numFrame);
    return virtualAlias;
  }
  const virtualAlias = `.virtual.${virtualInfo.alias}`;
  const originAlias = virtualInfo.alias;
  const loadingNode = getVirtualNode(lang, virtualInfo, virtualAlias).snapshot();
  useSafeExecute("remove", null, true)([originAlias]);
  useSafeExecute("addJson", null, false)({ alias: virtualAlias, json: loadingNode.toJson() });
  refreshVirtualNode(t, lang, virtualAlias, numFrame);
  return virtualAlias;
}

export async function recoverVirtualNode({ alias }: VirtualNodeInfo): Promise<BBox | undefined> {
  // 此方法直接修改了画板数据，没有进入操作栈，因此连续调用会爆炸！
  if (!isUndefined(alias) && BoardStore.board.getNodes().has(alias)) {
    const executer = BoardStore.board.executer;
    const bbox = BoardStore.graph.getExistingNode(alias).snapshot().bbox;

    // 否则，直接删除虚拟节点即可
    await new Remove(true).applyWith(executer, {
      aliases: [alias],
      params: undefined,
    });
    return bbox;
  }
}

type UploadImageOptions<T extends boolean> = {
  failed: () => Promise<void>;
  returnWH: T;
  noAudit?: boolean;
};
export type VirtualNodeInfo = {
  alias: string;
};
export type SrcInfo = {
  src: string;
  wh: { w: number; h: number };
  virtualNodeInfo?: VirtualNodeInfo[];
};
type StatusType = Omit<TaskType, "key" | "type" | "isKafka" | "taskPack">;

type BatchStatusItemType = {
  batch_id: string;
  engine_data: string; // JSON String
  push_status:
    | /** 等待推送 */ 0
    | /** 队列成功取消推送 */ 1
    | /** 推送成功 */ 2
    | /** 推送失败 */ 3
    | /** 执行成功  */ 4
    | /** 执行失败 */ 5;
  serial_number: string;
  wait_task: number;
};
type BatchStatusType = Array<BatchStatusItemType>;

type BatchTaskItem = {
  task: string;
  params: Dictionary<any>;
  serial_number: string;
  custom_embeddings?: string[];
  model_type: number;
};

export function calcTaskPending(taskStatus: BatchStatusItemType) {
  const { engine_data, wait_task } = taskStatus;
  let pending = wait_task;
  const engineData: {
    status: TaskType["status"];
    pending: TaskType["pending"];
    data: TaskDataType;
  } | null = engine_data ? JSON.parse(engine_data) : null;
  if (engineData) {
    pending = engineData.pending;
  }
  return pending;
}
type BatchTasks = Array<BatchTaskItem>;
export class SafeOp {
  static createBatchKafkaTask(params: {
    tasks: Array<{
      task: string;
      params: Dictionary<any>;
      serial_number: string;
      custom_embeddings?: string[];
      model_type: number;
    }>;
    batchId: string;
    bizType: GenerateBizType;
    width: number;
    height: number;
    failed: (e: any) => Promise<any>;
  }): Promise<{
    tasks: BatchTasks;
    batchId: string;
  } | void> {
    const { batchId, tasks, failed, bizType, width, height } = params;

    return fetch(`${parametersStore.creatorBatchKafkaHost}/task/push`, {
      method: "POST",
      headers: loginStore.jsonPostReqHeaders,
      body: JSON.stringify({
        tasks,
        batch_id: batchId,
        biz_type: bizType,
        width: Math.round(width),
        height: Math.round(height),
      }),
    })
      .then((res) => {
        if (!res.ok) return Promise.reject(res.text());
        return res.json();
      })
      .then((json) => {
        if (json.code !== 0) {
          return Promise.reject(json);
        }
        if (isUndefined(json?.data?.is_submit) || json?.data?.is_submit) {
          return {
            tasks,
            batchId,
          };
        } else {
          if (json?.data?.captcha) {
            events.emit(CaptchaEvent.SHOW, {
              type: CaptchaEvent.SHOW,
              data: {
                captcha: json.data.captcha,
              },
            });
            return Promise.reject(json);
          }
          return Promise.reject(json);
        }
      })
      .catch((e) => {
        failed(e);
      });
  }

  static getBatchKafkaStatus(
    batchId: string,
    serialNumbers: string[],
  ): Promise<{ data: BatchStatusType } | undefined> {
    return safeCall(
      async () => {
        const res = await fetch(`${parametersStore.creatorBatchKafkaHost}/task/query/queue`, {
          method: "POST",
          headers: loginStore.jsonPostReqHeaders,
          body: JSON.stringify({ batch_id: batchId, serial_numbers: serialNumbers }),
        });
        if (!res.ok) throw Error(await res.text());
        const json = await res.json();
        if (json.code === 0) {
          return json.data;
        } else {
          throw Error(json.msg);
        }
      },
      {
        success: async () => void 0,
        failed: async (e) =>
          Logger.error(`faied to execute 'getKafkaStatus' on '${batchId}' | ${e}`),
      },
    );
  }

  static createKafkaTask<T>(
    uri: string,
    data: Dictionary<any>,
    failed: (e: any) => Promise<T>,
  ): Promise<string | T> {
    return safeCall(
      async () => {
        const task = replaceAll(uri.slice(1, uri.length), "/", ".");
        const res = await fetch(`${parametersStore.creatorKafkaHost}/push`, {
          method: "POST",
          headers: loginStore.jsonPostReqHeaders,
          body: JSON.stringify({ task, params: data }),
        });
        if (!res.ok) throw Error(await res.text());
        const json = await res.json();
        return json["uid"] as string;
      },
      {
        success: async () => void 0,
        failed,
      },
    );
  }

  static async getKafkaStatus(
    taskId: string,
    forceGuest: boolean,
  ): Promise<StatusType | undefined> {
    return safeCall(
      async () => {
        const statusKey = forceGuest ? "status-guest" : "status";
        const res = await fetch(`${parametersStore.creatorKafkaHost}/${statusKey}/${taskId}`, {
          method: "GET",
          headers: loginStore.getReqHeaders,
        });
        if (!res.ok) throw Error(await res.text());
        return await res.json();
      },
      {
        success: async () => void 0,
        failed: async (e) =>
          Logger.error(
            `faied to execute 'getKafkaStatus' on '${taskId}' | forceGuest: ${forceGuest} | ${e}`,
          ),
      },
    );
  }

  static async getSuitableSrcInfo(
    t: ReturnType<typeof useToast>,
    lang: Lang,
    imageNode: IImageNode,
    maxWH: number,
    setup: (wh?: { w: number; h: number }) => Promise<VirtualNodeInfo[] | undefined>,
    failedFn: (virtualNodeInfo?: VirtualNodeInfo[]) => () => Promise<void>,
  ): Promise<SrcInfo | undefined> {
    const info = getImgWH(t, lang, imageNode.alias);
    if (!info) {
      return;
    }
    let src = imageNode.renderParams.src;
    let wh = info.imgWH;
    let virtualNodeInfo;
    if (wh.w * wh.h > maxWH ** 2) {
      const { w: nodeW, h: nodeH } = imageNode.wh;
      const nodeWH = { w: nodeW, h: Math.abs(nodeH) };
      if (nodeWH.w * nodeWH.h < wh.w * wh.h) {
        wh = nodeWH;
      }
      if (nodeWH.w * nodeWH.h <= maxWH ** 2) {
        /**
         * 此处把要提交的图片导出成符合限制大小的图片
         * 导出 -> 上传 -> 得到新 url 并替换
         */
        virtualNodeInfo = await setup(wh);
        const blob = await SafeOp.exportSingleNodeBlob(imageNode, { t, lang });
        if (!blob) return;
        const urls = await SafeOp.uploadImage(t, lang, blob, {
          failed: failedFn(virtualNodeInfo),
          returnWH: false,
        });
        if (!urls) return;
        src = urls.cdn;
      }
    }

    virtualNodeInfo = virtualNodeInfo ?? (await setup(wh));
    return { src, wh, virtualNodeInfo: virtualNodeInfo };
  }

  static async uploadImage<T extends boolean>(
    t: ReturnType<typeof useToast>,
    lang: Lang,
    blob: Blob,
    { failed, returnWH, noAudit }: UploadImageOptions<T>,
  ): Promise<
    (T extends true ? { urls: ImageURLs; wh: { w: number; h: number } } : ImageURLs) | void
  > {
    return safeCall(
      async () => {
        const urls = await uploadImage(getAIHost("prod"), blob, noAudit);
        if (!urls.safe) {
          toast(
            t,
            "warning",
            `${translate(Editor_Words["upload-image-not-safe-warning-message"], lang)}`,
          );
          throw Error;
        }
        if (!returnWH) return urls as any;
        const image = await safeCall(() => loadImage(blob), {
          success: async () => void 0,
          failed: async () => void 0,
        });
        if (!image) throw Error;
        return {
          urls,
          wh: { w: image.naturalWidth, h: image.naturalHeight },
        };
      },
      {
        success: async () => void 0,
        failed,
      },
    );
  }

  // manage busy status, and return the info of the virtualNode
  static async setupPostCreator(
    t: ReturnType<typeof useToast>,
    uri: string,
    lang: Lang,
    busyAliases: string[],
    virtualInfo: VirtualInfo[] | VirtualInfo,
    noToast?: boolean,
  ): Promise<VirtualNodeInfo[]> {
    tasksStore.addBusyNode(...busyAliases);
    if (!noToast) {
      toast(t, "success", translate(Editor_Words["submit-success-message"], lang));
    }

    const virtualInfoList = [];
    if (Array.isArray(virtualInfo)) {
      for (const virtualItemInfo of virtualInfo) {
        const item = {
          alias: await addVirtualNode(t, uri, lang, virtualItemInfo),
        };
        virtualInfoList.push(item);
      }
    } else {
      const item = {
        alias: await addVirtualNode(t, uri, lang, virtualInfo),
      };
      virtualInfoList.push(item);
    }
    return virtualInfoList;
  }

  static async cleanupSetup(
    busyAliases: string[],
    virtualNodeInfo?: VirtualNodeInfo[],
  ): Promise<Record<string, BBox> | void> {
    tasksStore.removeBusyNode(...busyAliases);
    if (virtualNodeInfo) {
      const bboxMap: Record<string, BBox> = {};
      for (const virtualNode of virtualNodeInfo) {
        const bbox = await recoverVirtualNode(virtualNode);
        if (bbox) {
          bboxMap[virtualNode.alias] = bbox;
        }
      }
      return bboxMap;
    }
  }

  static async setupBatchPostCreator(
    t: ReturnType<typeof useToast>,
    lang: Lang,
    busyAliases: string[],
    noToast?: boolean,
  ) {
    tasksStore.addBusyNode(...busyAliases);
    if (!noToast) {
      toast(t, "success", translate(Editor_Words["submit-success-message"], lang));
    }
  }

  static async cleanupSetupBatch(
    busyAliases: string[],
    virtualNodeInfo?: VirtualNodeInfo[],
  ): Promise<Record<string, BBox> | void> {
    tasksStore.removeBusyNode(...busyAliases);
    if (virtualNodeInfo) {
      const bboxMap: Record<string, BBox> = {};
      for (const virtualNode of virtualNodeInfo) {
        const bbox = await recoverVirtualNode(virtualNode);
        if (bbox) {
          bboxMap[virtualNode.alias] = bbox;
        }
      }
      return bboxMap;
    }
  }

  static async _postLocalCreator(
    t: ReturnType<typeof useToast>,
    uri: string,
    data: Dictionary<any>,
    lang: Lang,
    busyAliases: string[],
    virtualNodeInfo: VirtualNodeInfo,
    postprocess: (
      cdn: string,
      bbox: BBox,
      callback: () => Promise<void>,
      response: TaskDataType,
    ) => void,
  ): Promise<void> {
    const taskHash = `${getTaskHash(busyAliases)} | ${getRandomHash()}`;
    const task: TaskType = {
      key: taskHash,
      type: getTaskType(uri, data),
      status: "working",
      pending:
        tasksStore.tasks.size === 0
          ? 0
          : Math.max(...tasksStore.tasks.map((task) => task.pending)) + 1,
      isKafka: false,
      taskPack: { uri, data },
    };
    await safeCall(
      async () => {
        const res = await new Promise<Response>((resolve) => {
          const taskCallback: EventCallback = async (info) => {
            if (!info) return;
            const triggered = info.data as TaskType;
            if (triggered.key !== task.key) return;
            Events.getInstance().off(TrivialTaskEvent.EXEC, taskCallback);
            resolve(
              fetch(`${parametersStore.creatorHost}${uri}`, {
                method: "POST",
                headers: loginStore.jsonPostReqHeaders,
                body: JSON.stringify(data),
              }),
            );
          };
          // listen to event before adding task
          Events.getInstance().on(TrivialTaskEvent.EXEC, taskCallback);
          // add task, which will emit event
          tasksStore.addTask(task);
        });
        if (!res.ok) throw Error(await res.text());
        // upload to cdn
        const blob = await res.blob();
        const urls = await SafeOp.uploadImage(t, lang, blob, {
          failed: async () => void 0,
          returnWH: false,
          noAudit: uri === APIs_Img2Img.sr,
        });
        if (!urls) throw Error("`uploadImage` failed");
        // define callback to reset busy status
        async function callback(): Promise<void> {
          tasksStore.removeTask(task);
          tasksStore.removeBusyNode(...busyAliases);
          toast(t, "success", translate(Editor_Words["generate-success-message"], lang));
        }
        // remove virtual node
        const bbox = await recoverVirtualNode(virtualNodeInfo);
        if (!bbox) throw Error("`recoverVirtualNode` failed");
        // post process
        postprocess(urls.cdn, bbox, callback, urls);
      },
      {
        success: async () => void 0,
        failed: async (e) => {
          tasksStore.removeTask(task);
          await SafeOp.cleanupSetup(busyAliases, [virtualNodeInfo]);
          Logger.error(`faied to execute '_postLocalCreator' on '${uri}' | ${e}`);
          toast(t, "error", `${translate(Editor_Words["post-creator-error-message"], lang)}`);
        },
      },
    );
  }
  static async _postKafkaCreator(
    t: ReturnType<typeof useToast>,
    uri: string,
    data: Dictionary<any>,
    lang: Lang,
    busyAliases: string[],
    virtualNodeInfo: VirtualNodeInfo,
    postprocess: (
      cdn: string,
      bbox: BBox,
      callback: () => Promise<void>,
      response: TaskDataType,
    ) => void,
  ): Promise<void> {
    const failed = async (needToast = true) => {
      await SafeOp.cleanupSetup(busyAliases, [virtualNodeInfo]);
      needToast &&
        toast(t, "error", `${translate(Editor_Words["post-creator-error-message"], lang)}`);
    };
    // check task limit
    const kafkaTasks = tasksStore.tasks.filter((task) => task.isKafka);
    if (accelerationStore.taskLimit && kafkaTasks.length > accelerationStore.taskLimit) {
      const maxPending = Math.max(...kafkaTasks.map((task) => task.pending));
      if (maxPending > accelerationStore.maxPending) {
        toast(
          t,
          "warning",
          translate(Editor_Words["task-limit-message"], lang).replace(
            "$1",
            accelerationStore.taskLimit.toString(),
          ),
        );
        await SafeOp.cleanupSetup(busyAliases, [virtualNodeInfo]);
        return;
      }
    }
    // fetch taskId
    const taskId = await SafeOp.createKafkaTask(uri, data, failed);
    if (!taskId) {
      await failed();
      return;
    }
    // get initial task status
    const status = await SafeOp.getKafkaStatus(taskId, false);
    if (!status) {
      await failed();
      return;
    }
    const taskPolling = new TaskPolling({
      t,
      lang,
      key: taskId,
      type: getTaskType(uri, data),
      isKafka: true,
      taskPack: { uri, data },
      ...status,
    });
    // poll to get result
    const response = await polling(status, taskPolling);
    if (Logger.isDebug) {
      console.log("response: ", response);
    }
    if (!response) {
      await failed();
      return;
    }
    if (!response.safe) {
      toast(
        t,
        "warning",
        `${translate(Editor_Words["post-creator-not-safe-warning-message"], lang)}`,
      );
      await failed(false);
      return;
    }
    await safeCall(
      async () => {
        // define callback to reset busy status
        async function callback(): Promise<void> {
          tasksStore.removeBusyNode(...busyAliases);
          toast(t, "success", translate(Editor_Words["generate-success-message"], lang));
        }
        // remove virtual node
        const bbox = await recoverVirtualNode(virtualNodeInfo);
        if (!bbox) throw Error;
        // post process
        postprocess(response.cdn, bbox, callback, response);
      },
      {
        success: async () => void 0,
        failed,
      },
    );
  }

  static async _postBatchKafkaCreator({
    uri,
    data,
    generateCount,
    bizType,
    modelType,
    customEmbeddings,
    busyAliases,
    virtualNodeInfoList,
    postprocess,
  }: {
    uri: string;
    data: Dictionary<any>;
    generateCount: number;
    bizType: GenerateBizType;
    modelType:
      | 1 /** 画宇宙模型 */
      | 2 /** 画宇宙动漫模型 */
      | 3 /** 画宇宙小模型 */
      | 4 /** 画宇宙2.0模型 */;
    customEmbeddings?: string[];
    busyAliases: string[];
    virtualNodeInfoList?: VirtualNodeInfo[];
    postprocess?: (data: {
      cdn: string;
      bbox: BBox;
      virtualAlias?: string;
      callback: () => Promise<void>;
      response: TaskDataType;
    }) => void;
  }): Promise<void> {
    const lang = langStore.tgt;
    const failedBeforeStartBatch = async (e: any, virtualNodeInfoList: VirtualNodeInfo[]) => {
      await SafeOp.cleanupSetupBatch(busyAliases, virtualNodeInfoList);
      if (e?.data?.captcha) {
        toast(
          toastFn,
          "warning",
          translate(Editor_Words["post-creator-captcha-warn-message"], lang),
        );
        return;
      }
      if (e.msg) {
        toast(toastFn, "error", e.msg);
      } else {
        toast(toastFn, "error", `${translate(Editor_Words["post-creator-error-message"], lang)}`);
      }
    };
    // check task limit
    const kafkaTasks = tasksStore.tasks.filter((task) => task.isKafka);
    if (accelerationStore.taskLimit && kafkaTasks.length > accelerationStore.taskLimit) {
      const maxPending = Math.max(...kafkaTasks.map((task) => task.pending));
      if (maxPending > accelerationStore.maxPending) {
        toast(
          toastFn,
          "warning",
          translate(Editor_Words["task-limit-message"], lang).replace(
            "$1",
            accelerationStore.taskLimit.toString(),
          ),
        );
        await SafeOp.cleanupSetupBatch(busyAliases, virtualNodeInfoList);
        return;
      }
    }
    const batchId = generateUUID();
    const task = replaceAll(uri.slice(1, uri.length), "/", ".");

    const tasks: Array<{
      task: string;
      params: Dictionary<any>;
      serial_number: string;
      custom_embeddings?: string[];
      model_type: number;
    }> = range(0, generateCount).map((i) => {
      const seed = i === 0 ? data.seed : getRandomSeed();
      const _data = shallowCopy(data);
      const params = {
        ..._data,
      };

      if (i !== 0 && Array.isArray(_data.variations) && _data.variations.length > 0) {
        _data.variations[_data.variations.length - 1].seed = seed;
      } else {
        params.seed = seed;
      }
      return {
        task,
        params,
        serial_number: getRandomInt(1000000000, 10000000000).toString(),
        model_type: modelType,
        custom_embeddings: customEmbeddings,
      };
    });

    const nodeScale = data.highres_info ? data.highres_info?.upscale_factor ?? 2 : 1;
    const virtualInfo = {
      w: (data.wh?.w ?? data.w) * nodeScale,
      h: (data.wh?.h ?? data.h) * nodeScale,
    };
    console.log("virtualInfo: ", virtualInfo);

    const batchTasks: Array<BatchTaskType> = [];
    for (const i in tasks) {
      const task = tasks[i];
      const virtualNodeInfo = virtualNodeInfoList?.[i] ?? {
        alias: await addBatchVirtualNode(toastFn, uri, lang, virtualInfo, 10),
      };
      batchTasks.push({
        key: task.serial_number,
        type: getTaskType(uri, data),
        isKafka: true,
        taskPack: { uri, data: task.params },
        serialNumber: task.serial_number,
        batchId,
        status: "pending",
        pending: 0,
        forceGuest: false,
        executing: false,
        virtualNodeInfo: virtualNodeInfo,
      });
      await sleep(200);
    }

    // fetch taskId
    const createRes = await SafeOp.createBatchKafkaTask({
      tasks,
      batchId,
      bizType,
      width: virtualInfo.w,
      height: virtualInfo.h,
      failed: async (e) =>
        failedBeforeStartBatch(
          e,
          batchTasks.map((task) => task.virtualNodeInfo),
        ),
    });
    // 接口失败
    if (!createRes) return;

    const statusRes = await SafeOp.getBatchKafkaStatus(
      batchId,
      tasks.map((task) => task.serial_number),
    );
    const tasksStatus = statusRes?.data;

    const taskStatusMap =
      tasksStatus?.reduce<{ [key: string]: BatchStatusItemType }>((map, taskItem) => {
        map[taskItem.serial_number] = taskItem;
        return map;
      }, {}) ?? {};

    for (const i in batchTasks) {
      const batchTask = batchTasks[i];
      const taskStatus = taskStatusMap[batchTask.serialNumber];
      const pending = taskStatus ? calcTaskPending(taskStatus) : 0;
      batchTask.pending = pending;
    }
    const batchTaskPolling = new BatchTaskPolling({
      batchId,
      tasks: batchTasks,
    });

    tasksStore.addBatch(batchId);
    tasksStore.addBatchTasks(batchTaskPolling.tasks);

    const startPolling = async () => {
      console.log("startPolling");
      // poll to get result
      // 这里就是批量任务全部执行完成了
      batchGeneratePolling(batchTaskPolling, postprocess)
        .then(() => {
          console.log("批量任务全部执行", batchId);
          tasksStore.removeBusyNode(...busyAliases);
          // return SafeOp.cleanupSetupBatch(busyAliases);
        })
        .then(() => {
          toast(toastFn, "success", translate(Editor_Words["generate-success-message"], lang));
        })
        .catch((err) => {
          tasksStore.removeBusyNode(...busyAliases);
          if (err === "unsafe") {
            toast(
              toastFn,
              "warning",
              `${translate(Editor_Words["post-creator-not-safe-warning-message"], lang)}`,
            );
          } else {
            toast(
              toastFn,
              "error",
              `${translate(Editor_Words["post-creator-error-message"], lang)}`,
            );
          }
          console.error("batchGeneratePolling err: ", err);
        });
    };

    // 说明之前有别的任务
    // if (tasksStore.batchs[0] !== batchId) {
    //   const prevBatchCallback = (e?: EventInfo) => {
    //     console.log("prevBatchCallback: ", e);
    //     if (e?.data?.nextBatchId === batchId) {
    //       startPolling();
    //       Events.getInstance().off(BatchTaskEvent.FINISH, prevBatchCallback);
    //     }
    //   };
    //   Events.getInstance().on(BatchTaskEvent.FINISH, prevBatchCallback);
    // } else {
    startPolling();
    // }

    // @TODO 考虑把这些放到 batchPolling 的 callback 里
    // if (!response) {
    //   await failed();
    //   return;
    // }
    // if (!response.safe) {
    //   toast(
    //     toastFn,
    //     "warning",
    //     `${translate(Editor_Words["post-creator-not-safe-warning-message"], lang)}`,
    //   );
    //   await failed();
    //   return;
    // }
    // await safeCall(
    //   async () => {
    //     // define callback to reset busy status
    //     async function callback(): Promise<void> {
    //       tasksStore.removeBusyNode(...busyAliases);
    //       toast(toastFn, "success", translate(Editor_Words["generate-success-message"], lang));
    //     }
    //     // remove virtual node
    //     const bbox = await recoverVirtualNode(virtualNodeInfo);
    //     if (!bbox) throw Error;
    //     // post process
    //     postprocess(response.cdn, bbox, callback, response);
    //   },
    //   {
    //     success: async () => void 0,
    //     failed,
    //   },
    // );
  }
  static async postCreator({
    t,
    uri,
    meta,
    data,
    lang,
    busyAliases,
    virtualNodeInfo,
    postprocess,
    generateCount = 1,
    bizType,
  }: {
    t: ReturnType<typeof useToast>;
    uri: string;
    meta: MetaType;
    data: Dictionary<any>;
    lang: Lang;
    busyAliases: string[];
    virtualNodeInfo: VirtualNodeInfo[] | undefined;
    postprocess: (data: {
      cdn: string;
      bbox: BBox;
      virtualAlias?: string;
      callback: () => Promise<void>;
      response: TaskDataType;
    }) => void;
    generateCount?: number;
    bizType: GenerateBizType;
  }): Promise<void> {
    meta = shallowCopy(meta);
    const _data = shallowCopy(data);

    const postprocessWithRecord = (
      cdn: string,
      bbox: BBox,
      callback: () => Promise<void>,
      response: TaskDataType,
    ) => {
      postDrawRecord(cdn, response?.duration ?? -1, meta);
      postprocess({ cdn, bbox, callback, response });
    };

    if (parametersStore.useLocalServer) {
      if (!virtualNodeInfo) {
        const virtualInfo = { w: data.wh?.w ?? data.w, h: data.wh?.h ?? data.h };
        const virtualNodeAlias = await addBatchVirtualNode(toastFn, uri, lang, virtualInfo);
        virtualNodeInfo = [{ alias: virtualNodeAlias }];
      }

      if (_data.custom_embeddings) {
        _data.custom_embeddings = undefined;
        console.log("process custom_embeddings data", _data);
      }

      this._postLocalCreator(
        t,
        uri,
        _data,
        lang,
        busyAliases,
        virtualNodeInfo[0],
        postprocessWithRecord,
      );
      return;
    }
    const custom_embeddings = (meta as AIMetaType).data.custom_embeddings;
    // 非常 ugly 的判断
    let modelType:
      | 1 /** 画宇宙模型 */
      | 2 /** 画宇宙动漫模型 */
      | 3 /** 画宇宙小模型 */
      | 4 /** 画宇宙2.0模型 */ = 1;
    if (custom_embeddings?.length) {
      modelType = 3;
    } else if (
      (meta as AIMetaType).data.version === DREAMLIKE_VERSION ||
      (meta as AIMetaType).data.model
    ) {
      modelType = 4;
    } else if ((meta as AIMetaType).data?.is_anime) {
      modelType = 2;
    }
    const postprocessInBatchWithRecord = (data: {
      cdn: string;
      bbox: BBox;
      virtualAlias?: string | undefined;
      callback: () => Promise<void>;
      response: TaskDataType;
    }) => {
      postDrawRecord(data.cdn, data.response?.duration ?? -1, meta);
      postprocess(data);
    };
    await this._postBatchKafkaCreator({
      uri,
      data: _data,
      generateCount: generateCount ?? 1,
      bizType,
      modelType,
      customEmbeddings: custom_embeddings,
      busyAliases,
      virtualNodeInfoList: virtualNodeInfo,
      postprocess: postprocessInBatchWithRecord,
    });
  }

  static addNewImageGenerator(
    aliasPrefix: string,
    meta: MetaType,
  ): (data: {
    cdn: string;
    bbox: BBox;
    callback: () => Promise<void>;
    response: TaskDataType;
    virtualAlias?: string;
  }) => void {
    const cb = ({
      cdn,
      bbox,
      callback,
      response,
    }: {
      cdn: string;
      bbox: BBox;
      callback: () => Promise<void>;
      response: TaskDataType;
      virtualAlias?: string;
    }) => {
      const callbacks = {
        success: callback,
        failed: async () =>
          toast(
            toastFn,
            "error",
            translate(Editor_Words["add-image-error-message"], langStore.tgt),
          ),
      };

      const newAlias = `${aliasPrefix}.${getRandomHash()}`;
      addNewImage(newAlias, cdn, {
        info: bbox,
        meta: {
          type: meta.type,
          data: { ...meta.data, ...((response?.meta ?? meta.data) as any) },
        },
        callbacks,
      });
    };
    return cb;
  }

  static async exportBlob(
    nodes: ISingleNode[],
    { t, lang, ...others }: ExportBlobOptions & { t: ReturnType<typeof useToast>; lang: Lang },
  ): Promise<Blob | void> {
    return exportBlob(nodes, {
      failedCallback: async () =>
        toast(t, "error", translate(Editor_Words["export-blob-error-message"], lang)),
      ...others,
    });
  }

  static async exportSingleNodeBlob(
    node: ISingleNode,
    {
      t,
      lang,
      ...others
    }: ExportSingleNodeBlobOptions & { t: ReturnType<typeof useToast>; lang: Lang },
  ): Promise<Blob | void> {
    return exportSingleNodeBlob(node, {
      failedCallback: async () =>
        toast(t, "error", translate(Editor_Words["export-blob-error-message"], lang)),
      ...others,
    });
  }

  // cloud save stuffs
  static async getCloudProjects(
    pageIndex = 1,
    pageSize = 10,
    commitId = "",
  ): Promise<{ total: number; projects: CloudProjectType[] } | undefined> {
    return safeCall(
      async () => {
        if (loginStore.isGuest) return { total: 0, projects: [] };
        const url = `${
          process.env.REACT_APP_MAIN_URL
        }/ai_creator/cloud/list?page_index=${pageIndex}&page_size=${pageSize}${
          commitId ? "&commit_id=" + commitId : ""
        }`;
        const res = await fetch(url, {
          method: "GET",
          headers: loginStore.getForceAuthReqHeaders,
        });
        const { total, projects } = (await res.json()).data;
        return { total, projects: projects ?? [] };
      },
      {
        success: async () => void 0,
        failed: async () => void 0,
      },
    );
  }
  /** 暂时没用 */
  static async postToCloud({
    id,
    name,
    cloudCacheJson,
  }: {
    id: string;
    name?: string;
    cloudCacheJson?: string;
  }): Promise<PostCloudProjectResponse> {
    return safeCall(
      async () => {
        if (loginStore.isGuest) return true;
        const url = `${process.env.REACT_APP_MAIN_URL}/ai_creator/cloud/save`;
        return fetch(url, {
          method: "POST",
          headers: loginStore.jsonPostForceAuthReqHeaders,
          body: JSON.stringify({ project_id: id, project_name: name, content: cloudCacheJson }),
        }).then((res) => res.json());
        // return true;
      },
      {
        success: async () => void 0,
        failed: async () => false,
      },
    );
  }
  static async deleteCloudProject(id: string): Promise<boolean> {
    return safeCall(
      async () => {
        if (loginStore.isGuest) return true;
        const url = `${process.env.REACT_APP_MAIN_URL}/ai_creator/cloud/delete`;
        await fetch(url, {
          method: "POST",
          headers: loginStore.jsonPostForceAuthReqHeaders,
          body: JSON.stringify({ project_id: id }),
        });
        return true;
      },
      {
        success: async () => void 0,
        failed: async () => false,
      },
    );
  }

  static importGraph(graph: Graph, callbacks: BusinessOpCallbacks): void {
    const json = graph.clone().toJson();
    useSafeExecute("addGraph", null, true, callbacks)({ json, apiInfos: {} });
  }
  static replaceGraph(graph: Graph, callbacks: BusinessOpCallbacks): void {
    const json = graph.toJson();
    useSafeExecute("replaceGraph", null, true, callbacks)({ json, apiInfos: {} });
  }
  static async importCloudProject(
    project: CloudProjectType,
    callbacks: BusinessOpCallbacks,
  ): Promise<void> {
    const projectJson = await loadJson(project.content || project.url);
    this.importGraph(Graph.fromJsonInfo(projectJson.graphInfo), callbacks);
  }
  static async replaceCloudProject(project: CloudProjectType, callbacks?: BusinessOpCallbacks) {
    const success = async () => {
      safeClearExecuterStack();
      await callbacks?.success?.();
    };
    const projectJson = await loadJson(project.content || project.url);
    await this.replaceGraph(Graph.fromJsonInfo(projectJson.graphInfo), {
      success,
      failed: callbacks?.failed,
    });
  }
}
